import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Form } from 'antd';
import { useTranslation } from 'react-i18next';
import { useMachineDetailsRobotSchedule } from '../../context/MachineDetailsRobotScheduleContext';
import { ScheduleCreateModalProvider, useScheduleCreateModal } from '../../context/ScheduleCreateModalContext';
import {
  selectCreateScheduleIsLoading,
  selectUpdateScheduleIsLoading,
} from '../../../../../state/machineDetailsPanelRobot/robotSchedule/robotScheduleSelectors';
import { ScheduleUpdateConfirmModal } from '../ScheduleUpdateConfirmModal/ScheduleUpdateConfirmModal';
import { ExpectedCompletion } from './components/ExpectedCompletion/ExpectedCompletion';
import { RepeatingTask } from './components/RepeatingTask/RepeatingTask';
import { ScheduleFormInfo } from './components/ScheduleFormInfo/ScheduleFormInfo';
import { useScheduleCreateModalModalConfig } from './ScheduleCreateModal.modalConfig';
import {
  IconContainer,
  ScheduleAlert,
  ScheduleContainer,
  ScheduleCreateModalContainer,
} from './ScheduleCreateModal.styles';
import { SvgIcon } from 'lib/components/SvgIcon/SvgIcon';

interface ScheduleCreateModalContentProps {
  isB50: boolean;
  isScheduleCreateFullSizeOpen: boolean;
  hasOverlap: boolean;
}

const ScheduleCreateModalContent = ({
  isB50,
  isScheduleCreateFullSizeOpen,
  hasOverlap,
}: ScheduleCreateModalContentProps): JSX.Element => {
  const { t } = useTranslation();

  const { isReadOnly } = useMachineDetailsRobotSchedule();

  const {
    form,
    robot,
    hasScheduleChanges,
    handleSubmit,
    handleCancel,
    handleValidateFields,
    handleSaveChanges,
    handleFormValuesChange,
    scheduleName,
    isEditingScheduleName,
    isScheduleNameInvalid,
    handleToggleEditingScheduleName,
  } = useScheduleCreateModal();

  const isScheduleCreateLoading = useSelector(selectCreateScheduleIsLoading);
  const isScheduleUpdateLoading = useSelector(selectUpdateScheduleIsLoading);

  const effectiveModalConfig = useScheduleCreateModalModalConfig({
    form,
    robot,
    isScheduleCreateLoading,
    isScheduleUpdateLoading,
    handleCancel,
    hasScheduleChanges,
    handleValidateFields,
    handleSaveChanges,
    scheduleName,
    isEditingScheduleName,
    isScheduleNameInvalid,
    handleToggleEditingScheduleName,
  });

  const handleConfirmUpdate = (): void => {
    form.submit();
  };

  return (
    <>
      <Form
        component={false}
        form={form}
        requiredMark="optional"
        onFinish={handleSubmit}
        onValuesChange={handleFormValuesChange}
        disabled={isReadOnly}
      >
        <ScheduleCreateModalContainer
          $isScheduleCreateFullSizeOpen={isScheduleCreateFullSizeOpen}
          {...effectiveModalConfig}
        >
          {hasOverlap && (
            <ScheduleAlert
              icon={
                <IconContainer>
                  <SvgIcon name="warningNew" />
                </IconContainer>
              }
              message={t('scheduleCreateModal.hasEventOverlap')}
              type="warning"
              showIcon
            />
          )}

          <ScheduleContainer vertical>
            <ScheduleFormInfo isB50={isB50} />

            <RepeatingTask isB50={isB50} />

            <ExpectedCompletion isB50={isB50} />
          </ScheduleContainer>
        </ScheduleCreateModalContainer>
      </Form>

      <ScheduleUpdateConfirmModal isB50={isB50} handleConfirmUpdate={handleConfirmUpdate} />
    </>
  );
};

export const ScheduleCreateModal = (): JSX.Element => {
  const {
    isB50,
    handleCloseModal,
    robot,
    isCreateModalOpen,
    isEditModalOpen,
    selectedEvent,
    selectedCreateDateTime,
    isScheduleCreateFullSizeOpen,
    hasOverlap,
    updateScope,
    handleOpenUpdateConfirmModal,
  } = useMachineDetailsRobotSchedule();

  const providerProps = useMemo(
    () => ({
      robot,
      onClose: handleCloseModal,
      isOpen: isCreateModalOpen,
      isEventOpen: isEditModalOpen,
      selectedEvent,
      selectedCreateDateTime,
      isB50,
      updateScope,
      handleOpenUpdateConfirmModal,
    }),
    [
      robot,
      handleCloseModal,
      isCreateModalOpen,
      isEditModalOpen,
      selectedEvent,
      selectedCreateDateTime,
      isB50,
      updateScope,
      handleOpenUpdateConfirmModal,
    ]
  );

  return (
    <ScheduleCreateModalProvider {...providerProps}>
      <ScheduleCreateModalContent
        isB50={isB50}
        isScheduleCreateFullSizeOpen={isScheduleCreateFullSizeOpen}
        hasOverlap={hasOverlap}
      />
    </ScheduleCreateModalProvider>
  );
};
